/**
 * Pure auto-selection logic for the Host (Zoom) field.
 *
 * Priority order (sch-28 — kept reversible):
 *   1. If exactly one valid interviewer is preselected → that interviewer.
 *   2. Otherwise, if the current user is a valid host → current user.
 *   3. Otherwise → null (silent "no prefill" — empty field, no error).
 *
 * `planHostType` from the interview plan ('organizer' | 'interviewer') is
 * a tiebreaker for ambiguous cases:
 *   - planHostType === 'organizer' flips the order: current user is tried
 *     first, falling back to a single valid interviewer.
 *   - planHostType === 'interviewer' uses the default order above.
 *   - Anything else (or undefined) uses the default order.
 *
 * "Valid" = `can_host_zoom_meeting === true` AND `!zoom_rate_limit_reached`
 * AND zoom_plan ∉ {4, 99} (those mean Unassigned / None — needs license).
 * `zoom_status === undefined` is treated as "unknown → not eligible for
 * auto-pick", so adhoc-create with no plan response never auto-fills the
 * recruiter (matches legacy gating on `interviewPlan?.can_host_zoom_meeting`).
 *
 * Reversibility: to flip the default order in future, swap the two branches
 * inside `pickByPriorityOrder` (or change `defaultOrder` below).
 */
import type { HostZoomStatus } from '../fields/HostRenders';

interface AutoHostInputOption {
  value: string;
  zoomStatus?: HostZoomStatus;
}

interface AutoHostInputUser {
  id: number | string;
  zoomStatus?: HostZoomStatus;
}

export interface PickAutoHostIdArgs {
  participantOptions: AutoHostInputOption[];
  currentUser?: AutoHostInputUser;
  /** Plan-level Zoom host strategy from `_interview.json.jbuilder`. */
  planHostType?: 'organizer' | 'interviewer' | string | null;
}

/** Returns true when the user is *definitely* allowed to host. Undefined
 *  status returns false — auto-pick should never propose an unknown user. */
export function isValidHost(status: HostZoomStatus | undefined): boolean {
  if (!status) return false;
  if (status.can_host_zoom_meeting !== true) return false;
  if (status.zoom_rate_limit_reached === true) return false;
  if (status.zoom_plan === 4 || status.zoom_plan === 99) return false;
  return true;
}

/** Returns true ONLY when the BE has explicitly said the user can't host.
 *  `undefined` status returns false (don't clear data we haven't loaded
 *  yet). Used to decide whether to override an existing `host_id` that
 *  came from edit hydration or BE pre-fill but has since gone stale. */
export function isExplicitlyInvalidHost(
  status: HostZoomStatus | undefined
): boolean {
  if (!status) return false;
  if (status.can_host_zoom_meeting === false) return true;
  if (status.zoom_rate_limit_reached === true) return true;
  if (status.zoom_plan === 4 || status.zoom_plan === 99) return true;
  return false;
}

type PriorityCandidate = 'interviewer' | 'currentUser';

/** Default priority: interviewer-first (when exactly one valid is picked),
 *  then current user. Tweak this single constant to flip the project-wide
 *  default. */
const defaultOrder: PriorityCandidate[] = ['interviewer', 'currentUser'];

function pickFromOrder(
  order: PriorityCandidate[],
  validInterviewers: AutoHostInputOption[],
  currentUser?: AutoHostInputUser
): string | null {
  for (const candidate of order) {
    if (candidate === 'interviewer') {
      // Pick the first valid interviewer regardless of count. Panel
      // interviews with `zoom_host_type=interviewer` expect ONE of the
      // attendees to host — picking the first valid is the only
      // deterministic rule when the plan doesn't specify which.
      if (validInterviewers.length >= 1) return validInterviewers[0].value;
    } else if (candidate === 'currentUser') {
      if (currentUser && isValidHost(currentUser.zoomStatus)) {
        return String(currentUser.id);
      }
    }
  }
  return null;
}

export function pickAutoHostId(args: PickAutoHostIdArgs): string | null {
  const { participantOptions, currentUser, planHostType } = args;
  const validInterviewers = participantOptions.filter((o) =>
    isValidHost(o.zoomStatus)
  );

  // Plan-level hint only flips the order — the candidate set is identical.
  const order: PriorityCandidate[] =
    planHostType === 'organizer'
      ? ['currentUser', 'interviewer']
      : defaultOrder;

  return pickFromOrder(order, validInterviewers, currentUser);
}
